import React, { ComponentProps, useState } from 'react';
import { asFetchableBlock } from '../../_lib/BlockFetcher';
import { BlockType } from '../../types/api/ElementType';
import { DefaultView, DeleteAction, OptionalEditView, Views } from '../../types/BlockWithViews';
import { UserImportConfigurationEditView } from './views/UserImportConfigurationEditView';
import { useAppContext } from '../../contexts/AppContext/AppContext';
import { UserImportConfigurationDefaultView } from './views/UserImportConfigurationDefaultView';
import { BlockBar, BlockBarAnchor, BlockBarButton } from '../../_lib/components/BlockBar';
import { useSonTranslation } from '../../translation/useSonTranslation';
import { Icons } from '../../components/atoms/SVG';
import { useHtmlValidId } from '../../hooks/useHtmlValidId';
import ModalDialog from '../../components/organisms/ModalDialog/ModalDialog';
import { AcceptDeclineForm, SonForm } from '../../_lib/Form2';

type UserImportConfiguration = Views<
  DefaultView<UserImportConfigurationDefaultView> &
  OptionalEditView<UserImportConfigurationEditView> &
  DeleteAction<{ form: SonForm<any> }>
>;

function UserImportConfiguration({ views, activeView }: UserImportConfiguration) {
  const { t } = useSonTranslation('UserImportConfiguration');
  const id = useHtmlValidId();
  const { editModeInfo } = useAppContext();
  const [currentActiveView, setCurrentActiveView] = useState(editModeInfo.active
    ? (activeView || 'default')
    : 'default');
  const [showDeletModal, setShowDeleteModal] = useState(false);

  return (
    <>
      <BlockBar heading={t('blockHeading')} hierarchy={3} modifier="border-bottom">
        {editModeInfo.active
          ? <>
              <BlockBarAnchor
                text={t('editLabel')}
                svgSpriteId={Icons.EDIT_PENCIL}
                aria-controls={id}
                onClick={() => {
                  if (currentActiveView !== 'default') {
                    setCurrentActiveView('default');
                  } else {
                    setCurrentActiveView('edit');
                  }
                }}
              />
              <BlockBarButton
                text={t('delete.lable')}
                svgSpriteId={Icons.ERASER}
                onClick={() => setShowDeleteModal(!showDeletModal)}
              />
            </>
          : null}
      </BlockBar>
      <div id={id} className="block-bar__controlled-container">
        { currentActiveView === 'default' ? <UserImportConfigurationDefaultView {...views.default} /> : null }
        { currentActiveView === 'edit' && views.edit
          ? <UserImportConfigurationEditView {...views.edit} />
          : null}
      </div>
      {showDeletModal && views.delete?.embedded?.form
        ? <ModalDialog closeModal={() => setShowDeleteModal(false)} headline={t('delete.lable')}>
            <AcceptDeclineForm
              form={views.delete.embedded.form}
              headline={t('delete.question')}
              messageSuccess={t('delete.status.success')}
              messageSending={t('delete.status.sending')}
              messageFail={t('delete.status.fail')}
            />
          </ModalDialog>
        : null}
    </>
  );
}

export const UserImportConfigurationBlock =
  asFetchableBlock(UserImportConfiguration, BlockType.userimportconfiguration);
export type UserImportConfigurationBlock = ComponentProps<typeof UserImportConfigurationBlock>;
